import React, { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import axios from "axios";
import { API, setShopToken } from "@/lib/api";
import { KeyRound } from "lucide-react";

const KEYS = ["1","2","3","4","5","6","7","8","9","*","0","<"];

export function PinModal({ open, onOpenChange, slug, intent = "input", onSuccess }) {
  const { t } = useLang();
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (!open) { setPin(""); setErr(""); } }, [open]);

  const subtitle = intent === "admin" ? t("pin.subtitle_admin")
    : intent === "reports" ? t("pin.subtitle_reports")
    : t("pin.subtitle_input");

  const submit = async (candidate) => {
    setErr(""); setLoading(true);
    try {
      const r = await axios.post(`${API}/shops/by-slug/${slug}/verify-pin`, { pin: candidate });
      const { token, role, person_name, person_id } = r.data;
      // enforce role for intent
      const needsManager = intent === "admin" || intent === "reports";
      if (needsManager && !["owner","manager"].includes(role)) {
        setErr(t("pin.invalid"));
        setLoading(false); setPin("");
        return;
      }
      setShopToken(slug, token, role, person_name, person_id);
      toast.success(`Welcome, ${person_name}`);
      onOpenChange(false);
      onSuccess?.(role, person_name);
    } catch (e) {
      setErr(e.response?.data?.detail || t("pin.invalid"));
      setPin("");
    } finally { setLoading(false); }
  };

  const press = (k) => {
    if (loading) return;
    if (k === "<") { setPin(p => p.slice(0, -1)); return; }
    if (k === "*") { setPin(""); return; }
    if (pin.length >= 4) return;
    const next = pin + k;
    setPin(next);
    if (next.length === 4) submit(next);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid="pin-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-emerald-800" /> {t("pin.title")}
          </DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>
        <div className="flex justify-center gap-2 py-3" data-testid="pin-display">
          {[0,1,2,3].map(i => (
            <div key={i} className={`w-12 h-14 rounded-md border-2 flex items-center justify-center font-mono text-2xl ${pin[i] ? "border-emerald-800 bg-emerald-50" : "border-stone-300 bg-stone-50"}`}>
              {pin[i] ? "•" : ""}
            </div>
          ))}
        </div>
        {err && <div className="text-sm text-red-600 text-center" data-testid="pin-error">{err}</div>}
        <div className="grid grid-cols-3 gap-2 mt-2">
          {KEYS.map(k => (
            <button
              key={k}
              onClick={() => press(k)}
              className="h-14 rounded-md border border-stone-200 bg-white hover:bg-stone-50 active:scale-[0.98] transition font-mono text-xl text-stone-800"
              data-testid={`pin-key-${k === "<" ? "back" : k === "*" ? "clear" : k}`}
            >
              {k === "<" ? "⌫" : k === "*" ? "C" : k}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
